import { CFG } from '../config';
import { sumMacros } from '../utils';
import type { DayData, StravaActivity } from '../types';

interface Props {
  days: DayData[];
  activities: StravaActivity[];
  calError: boolean;
}

interface Metric {
  value: string;
  label: string;
  cls: string;
}

interface ScoreResult {
  score: number;
  grade: string;
  title: string;
  cls: string;
  metrics: Metric[];
  reasons: string[];
}

function tierClass(val: number, good: number, warn: number): string {
  return val >= good ? 'vm-good' : val >= warn ? 'vm-warn' : 'vm-bad';
}

function computeScore(days: DayData[], activities: StravaActivity[]): ScoreResult {
  const { calories, protein, daysWithData } = sumMacros(days);
  const metrics: Metric[] = [];
  const reasons: string[] = [];
  let score = 0;

  if (daysWithData > 0) {
    const avgCal = calories / daysWithData;
    const avgP = protein / daysWithData;
    const [lo, hi] = CFG.calRange;

    score += Math.min(daysWithData / 7 * 40, 40);
    if (daysWithData >= 6) reasons.push('записи почти каждый день');
    else if (daysWithData >= 4) reasons.push(`записи ${daysWithData}/7 дней`);
    else reasons.push(`мало записей: ${daysWithData}/7`);
    metrics.push({ value: `${daysWithData}/7`, label: 'дней', cls: tierClass(daysWithData, 5, 3) });

    if (avgCal >= lo && avgCal <= hi) { score += 25; reasons.push(`калории в целевом диапазоне (${lo}-${hi})`); }
    else if (avgCal >= lo - 200 && avgCal <= hi + 200) { score += 15; reasons.push('калории близко к норме'); }
    else reasons.push(avgCal < lo ? `калории ниже цели ${lo}` : `калории выше цели ${hi}`);
    metrics.push({
      value: String(Math.round(avgCal)),
      label: `из ${CFG.targetCal} ккал`,
      cls: avgCal >= lo && avgCal <= hi ? 'vm-good' : avgCal >= lo - 200 ? 'vm-warn' : 'vm-bad',
    });

    if (avgP >= CFG.protein * 0.85) { score += 20; reasons.push('белок в норме'); }
    else if (avgP >= CFG.protein * 0.6) { score += 10; reasons.push(`белок ниже цели (${CFG.protein}г)`); }
    else reasons.push(`белок сильно ниже цели (${CFG.protein}г)`);
    metrics.push({
      value: `${Math.round(avgP)}g`,
      label: `из ${CFG.protein}g белка`,
      cls: tierClass(avgP, CFG.protein * 0.85, CFG.protein * 0.6),
    });
  }

  if (activities.length >= 3) score += 15;
  else if (activities.length >= 1) score += 8;
  metrics.push({
    value: String(activities.length),
    label: 'тренировок',
    cls: tierClass(activities.length, 3, 1),
  });

  let grade: string, title: string, cls: string;
  if (score >= 80) { grade = 'A'; title = 'Отличная неделя.'; cls = 'grade-a'; }
  else if (score >= 60) { grade = 'B'; title = 'Неплохо, но есть куда расти.'; cls = 'grade-b'; }
  else if (score >= 35) { grade = 'C'; title = 'Средненько. Можно лучше.'; cls = 'grade-c'; }
  else { grade = 'D'; title = daysWithData ? 'Слабая неделя. Давай соберёмся.' : 'Нет данных. Начни записывать.'; cls = 'grade-d'; }

  return { score, grade, title, cls, metrics, reasons };
}

export function Verdict({ days, activities, calError }: Props) {
  if (calError) {
    return (
      <div class="verdict grade-offline">
        <div class="v-top">
          <div class="v-grade">--</div>
          <div class="v-title">API недоступен</div>
        </div>
      </div>
    );
  }

  const { grade, title, cls, metrics, reasons } = computeScore(days, activities);

  return (
    <div class={`verdict ${cls}`}>
      <div class="v-top">
        <div class="v-grade">{grade}</div>
        <div class="v-title">{title}</div>
      </div>
      <div class="v-sub">{reasons.join(' / ')}</div>
      <div class="v-metrics">
        {metrics.map((m, i) => (
          <div key={i} class="v-metric">
            <div class={`vm-val ${m.cls}`}>{m.value}</div>
            <div class="vm-label">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
